import {
  AssetData,
  ContentItem,
  PressMentionsSectionItemData,
  SectionTypeStructureFieldList,
  TestimonialsSectionItemData,
} from '@betterfunnel/types';
import {
  DocumentDuplicateIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import * as Sentry from '@sentry/react';
import { QueryDocumentSnapshot } from 'firebase/firestore';
import { useCallback, useContext, useMemo, useState } from 'react';
import { uid } from 'uid';
import Button from '../components/Common/Button';
import IconButton from '../components/Common/IconButton';
import MenuButton from '../components/Common/MenuButton';
import { ImageViewerModal } from '../components/ImageViewer/ImageViewerModal';
import QuillField from '../components/QuillField';
import { EditStructuredSectionItemModal } from '../components/SectionStructureForm/EditStructuredSectionItemModal';
import { AccountContentContext } from '../contexts/AccountContentContext';
import { useDashboardContext } from '../contexts/DashboardContext';
import { useSnackbarContext } from '../contexts/SnackbarContext';
import { useHasFeature } from '../hooks/useHasFeature';
import CreateComparisonCategoryModal from '../modals/CreateComparisonCategoryModal';
import ImageLibraryModal from '../modals/ImageLibraryModal/ImageLibraryModal';
import { extractTextFromQuillTrimmed } from '../utils/quill';

export default function Content() {
  const { show, hide } = useSnackbarContext();
  const hasComparisonListsFeature = useHasFeature('comparisonLists');
  const { accountConfig, updateAccountConfig, getSectionTypeConfig } =
    useDashboardContext();
  const { contents, createContentItem, updateContentItem, deleteContentItem } =
    useContext(AccountContentContext);

  const [selectImageModalOpen, setSelectImageModalOpen] = useState<string>();
  const [testimonialModalOpen, setTestimonialModalOpen] = useState(false);
  const [testimonial, setTestimonial] = useState<TestimonialsSectionItemData>();

  const [pressMentionModalOpen, setPressMentionModalOpen] = useState(false);
  const [pressMention, setPressMention] =
    useState<TestimonialsSectionItemData>();

  const [comparisonCategory, setComparisonCategory] =
    useState<QueryDocumentSnapshot<ContentItem, ContentItem>>();
  const [comparisonItem, setComparisonItem] =
    useState<QueryDocumentSnapshot<ContentItem, ContentItem>>();
  const [comparisonCategoryModalOpen, setComparisonCategoryModalOpen] =
    useState(false);
  const [comparisonItemModalOpen, setComparisonItemModalOpen] = useState(false);

  const comparisonCategories = useMemo(
    () => contents?.filter((c) => c.data().type === 'comparisonCategory'),
    [contents],
  );
  const comparisonItems = useMemo(
    () => contents?.filter((c) => c.data().type === 'comparisonItem'),
    [contents],
  );

  const comparisonListItemField = useMemo(
    () =>
      getSectionTypeConfig('comparisonList')?.structure.fields?.find(
        (f) => f.type === 'list' && f.name === 'items',
      ) as SectionTypeStructureFieldList | undefined,
    [getSectionTypeConfig],
  );

  const testimonialItemField = useMemo(
    () =>
      getSectionTypeConfig('testimonials')?.structure.fields?.find(
        (f) => f.type === 'list' && f.name === 'testimonials',
      ) as SectionTypeStructureFieldList | undefined,
    [getSectionTypeConfig],
  );

  const pressMentionItemField = useMemo(
    () =>
      getSectionTypeConfig('pressMentions')?.structure.fields?.find(
        (f) => f.type === 'list' && f.name === 'pressMentions',
      ) as SectionTypeStructureFieldList | undefined,
    [getSectionTypeConfig],
  );

  const onHeroImageUploaded = useCallback(
    (newFiles?: AssetData[]) => {
      updateAccountConfig({
        defaultHeroImages: [
          ...(accountConfig?.defaultHeroImages || []),
          ...(newFiles || []).map((a) => ({ ...a, id: uid(12) })),
        ],
      });
      show('Uploaded');
    },
    [accountConfig?.defaultHeroImages, show, updateAccountConfig],
  );

  const onHeroImageDeleted = useCallback(
    (image: AssetData) => {
      updateAccountConfig({
        defaultHeroImages: [
          ...(accountConfig?.defaultHeroImages || []).filter(
            (i) => i.id !== image.id,
          ),
        ],
      });
      show('Deleted');
    },
    [accountConfig?.defaultHeroImages, show, updateAccountConfig],
  );

  const onClientLogosUploaded = useCallback(
    (newFiles?: AssetData[]) => {
      updateAccountConfig({
        defaultClientLogos: [
          ...(accountConfig?.defaultClientLogos || []),
          ...(newFiles || []).map((a) => ({ ...a, id: uid(12) })),
        ],
      });
      show('Uploaded');
    },
    [accountConfig?.defaultClientLogos, show, updateAccountConfig],
  );

  const onClientLogoDeleted = useCallback(
    (image: AssetData) => {
      updateAccountConfig({
        defaultClientLogos: [
          ...(accountConfig?.defaultClientLogos || []).filter(
            (i) => i.id !== image.id,
          ),
        ],
      });
      show('Deleted');
    },
    [accountConfig?.defaultClientLogos, show, updateAccountConfig],
  );

  return (
    <div className="py-8">
      <EditStructuredSectionItemModal
        item={testimonial}
        field={testimonialItemField}
        open={testimonialModalOpen}
        onClose={() => {
          setTestimonial(undefined);
          setTestimonialModalOpen(false);
        }}
        onSave={(newTestimonial) => {
          const newTestimonials = [...(accountConfig?.testimonials ?? [])];
          const index = newTestimonials.findIndex(
            (t) => t.id === newTestimonial.id,
          );
          if (index !== -1) newTestimonials[index] = newTestimonial;
          else newTestimonials.push(newTestimonial);

          updateAccountConfig({
            testimonials: newTestimonials,
          });
          show(index === -1 ? 'Created' : 'Updated');
          setTestimonial(undefined);
          setTestimonialModalOpen(false);
        }}
      />
      <EditStructuredSectionItemModal
        item={pressMention}
        open={pressMentionModalOpen}
        field={pressMentionItemField}
        onClose={() => {
          setPressMention(undefined);
          setPressMentionModalOpen(false);
        }}
        onSave={(newPressMention) => {
          const newPressMentions = [...(accountConfig?.pressMentions ?? [])];
          const index = newPressMentions.findIndex(
            (t) => t.id === newPressMention.id,
          );
          if (index !== -1) newPressMentions[index] = newPressMention;
          else newPressMentions.push(newPressMention);

          updateAccountConfig({
            pressMentions: newPressMentions,
          });
          show(index === -1 ? 'Created' : 'Updated');
          setPressMention(undefined);
          setPressMentionModalOpen(false);
        }}
      />
      <CreateComparisonCategoryModal
        item={
          comparisonCategory
            ? { ...comparisonCategory.data(), id: comparisonCategory.id }
            : undefined
        }
        open={comparisonCategoryModalOpen}
        onClose={() => {
          setComparisonCategory(undefined);
          setComparisonCategoryModalOpen(false);
        }}
        onSave={async (newItem) => {
          return (
            comparisonCategory
              ? updateContentItem(comparisonCategory.id, newItem)
              : createContentItem({ ...newItem, type: 'comparisonCategory' })
          )
            .then(() => {
              setComparisonCategory(undefined);
              setComparisonCategoryModalOpen(false);
              show(!comparisonCategory ? 'Created' : 'Updated');
            })
            .catch((e) => {
              Sentry.captureException(e);
              show(e.toString(), 'warn');
            });
        }}
      />

      <EditStructuredSectionItemModal
        item={
          comparisonItem
            ? { ...comparisonItem.data(), id: comparisonItem.id }
            : undefined
        }
        field={comparisonListItemField}
        open={comparisonItemModalOpen}
        onClose={() => {
          setComparisonCategory(undefined);
          setComparisonItem(undefined);
          setComparisonItemModalOpen(false);
        }}
        onSave={async (newItem) => {
          return (
            comparisonItem
              ? updateContentItem(comparisonItem.id, {
                  ...newItem,
                  type: 'comparisonItem',
                })
              : createContentItem({
                  ...newItem,
                  type: 'comparisonItem',
                  category: comparisonCategory?.id,
                })
          )
            .then(() => {
              setComparisonCategory(undefined);
              setComparisonItem(undefined);
              setComparisonItemModalOpen(false);
              show(!comparisonItem ? 'Created' : 'Updated');
            })
            .catch((e) => {
              Sentry.captureException(e);
              show(e.toString(), 'warn');
            });
        }}
      />

      <ImageLibraryModal
        multi
        open={!!selectImageModalOpen}
        onClose={() => setSelectImageModalOpen(undefined)}
        onSelected={(image) => {
          const mode = selectImageModalOpen;
          setSelectImageModalOpen(undefined);

          if (mode === 'hero') {
            onHeroImageUploaded(image);
          } else if (mode === 'logo') {
            onClientLogosUploaded(image);
          }
        }}
      />
      <div className="pb-8 sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-base font-semibold leading-6 text-gray-900">
            Content
          </h1>
          <p className="mt-2 text-sm text-gray-700">
            Add your content, for us to use in your landing pages.
          </p>
        </div>
      </div>

      <div className="mb-6">
        <div className="flex items-end justify-between">
          <div className="flex flex-col">
            <h2 className="text-base font-semibold leading-7 text-gray-900">
              Hero images
            </h2>
            <p className="mt-1 text-sm leading-6 text-gray-500">
              Add your hero images.
            </p>
          </div>
          <Button onClick={() => setSelectImageModalOpen('hero')}>
            Add Hero Image
          </Button>
        </div>

        <dl className="mt-6 space-y-6 divide-y divide-gray-100 border-t border-gray-200 text-sm leading-6">
          <div className="pt-6 sm:flex">
            <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">
              Hero images
            </dt>
            <div className="w-full">
              <ImagesList
                images={accountConfig?.defaultHeroImages}
                onDeleteImageClick={onHeroImageDeleted}
              />
            </div>
          </div>
        </dl>
      </div>

      <div className="mb-6 mt-12">
        <div className="flex items-end justify-between">
          <div className="flex flex-col">
            <h2 className="text-base font-semibold leading-7 text-gray-900">
              Client logos
            </h2>
            <p className="mt-1 text-sm leading-6 text-gray-500">
              Add your client logos.
            </p>
          </div>
          <Button onClick={() => setSelectImageModalOpen('logo')}>
            Add Client Logo
          </Button>
        </div>

        <dl className="mt-6 space-y-6 divide-y divide-gray-100 border-t border-gray-200 text-sm leading-6">
          <div className="pt-6 sm:flex">
            <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">
              Client logos
            </dt>
            <div className="w-full">
              <ImagesList
                images={accountConfig?.defaultClientLogos}
                onDeleteImageClick={onClientLogoDeleted}
              />
            </div>
          </div>
        </dl>
      </div>

      <div className="mb-6 mt-12">
        <div className="flex items-end justify-between">
          <div className="flex flex-col">
            <h2 className="text-base font-semibold leading-7 text-gray-900">
              Testimonials
            </h2>
            <p className="mt-1 text-sm leading-6 text-gray-500">
              Add your company&apos;s testimonials.
            </p>
          </div>
          <Button onClick={() => setTestimonialModalOpen(true)}>
            Add Testimonial
          </Button>
        </div>

        <dl className="mt-6 space-y-6 divide-y divide-gray-100 border-t border-gray-200 text-sm leading-6">
          <div className="pt-6 sm:flex">
            <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">
              Testimonials
            </dt>
            <div className="w-full">
              <Testimonials
                testimonials={accountConfig?.testimonials}
                onEditClick={(testimonial) => {
                  setTestimonial(testimonial);
                  setTestimonialModalOpen(true);
                }}
                onDeleteClick={(testimonial) => {
                  updateAccountConfig({
                    testimonials: accountConfig?.testimonials?.filter(
                      (t) => t.id !== testimonial.id,
                    ),
                  });
                  show('Deleted');
                }}
              />
            </div>
          </div>
        </dl>
      </div>
      <div className="mb-6 mt-12">
        <div className="flex items-end justify-between">
          <div className="flex flex-col">
            <h2 className="text-base font-semibold leading-7 text-gray-900">
              Press Mentions
            </h2>
            <p className="mt-1 text-sm leading-6 text-gray-500">
              Add your company&apos;s press mentions.
            </p>
          </div>
          <Button onClick={() => setPressMentionModalOpen(true)}>
            Add Press Mention
          </Button>
        </div>

        <dl className="mt-6 space-y-6 divide-y divide-gray-100 border-t border-gray-200 text-sm leading-6">
          <div className="pt-6 sm:flex">
            <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">
              Press Mentions
            </dt>
            <div className="w-full">
              <PressMentions
                pressMentions={accountConfig?.pressMentions || []}
                onEditClick={(pressMention) => {
                  setPressMention(pressMention);
                  setPressMentionModalOpen(true);
                }}
                onDeleteClick={(pressMention) => {
                  updateAccountConfig({
                    pressMentions: accountConfig?.pressMentions?.filter(
                      (t) => t.id !== pressMention.id,
                    ),
                  });
                  show('Deleted');
                }}
              />
            </div>
          </div>
        </dl>
      </div>
      {hasComparisonListsFeature ? (
        <div className="mb-6 mt-12">
          <div className="flex items-end justify-between">
            <div className="flex flex-col">
              <h2 className="text-base font-semibold leading-7 text-gray-900">
                Comparison lists
              </h2>
              <p className="mt-1 text-sm leading-6 text-gray-500">
                Add your comparison lists by category.
              </p>
            </div>

            <Button onClick={() => setComparisonCategoryModalOpen(true)}>
              Add Category
            </Button>
          </div>

          {comparisonCategories?.map((category) => (
            <dl
              key={category.id}
              className="mt-6 space-y-6 divide-y divide-gray-100 border-t border-gray-200 text-sm leading-6"
            >
              <div>
                <div className="flex justify-between pt-6">
                  <dt className="flex flex-col font-medium text-gray-700 sm:w-64 sm:pr-6">
                    <span>{category.data().name}</span>
                    <span className="text-xs text-gray-500">Category</span>
                  </dt>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="secondary"
                      color="primary"
                      onClick={() => {
                        setComparisonCategory(category);
                        setComparisonItem(undefined);
                        setComparisonItemModalOpen(true);
                      }}
                    >
                      Add Item
                    </Button>
                    <MenuButton
                      items={[
                        {
                          type: 'button',
                          title: 'Edit',
                          onClick: () => {
                            setComparisonCategory(category);
                            setComparisonCategoryModalOpen(true);
                          },
                        },
                        {
                          type: 'button',
                          title: 'Delete',
                          destructive: true,
                          onClick: () => {
                            deleteContentItem(category.id);
                            show('Deleted!');
                          },
                        },
                      ]}
                    ></MenuButton>
                  </div>
                </div>
                <div className="w-full">
                  <ComparisonItems
                    items={
                      comparisonItems?.filter(
                        (c) => c.data().category === category.id,
                      ) || []
                    }
                    onEditClick={(item) => {
                      setComparisonCategory(category);
                      setComparisonItem(item);
                      setComparisonItemModalOpen(true);
                    }}
                    onDuplicateClick={(item) => {
                      const snack = show('Duplicating...');
                      createContentItem({
                        ...item.data(),
                        id: uid(12),
                      })
                        .then(() => {
                          hide(snack.id);
                          show('Duplicated');
                        })
                        .catch((e) => {
                          hide(snack.id);
                          Sentry.captureException(e);
                          show(e.toString(), 'warn');
                        });
                    }}
                    onDeleteClick={(item) => {
                      deleteContentItem(item.id);
                      show('Deleted');
                    }}
                  />
                </div>
              </div>
            </dl>
          ))}
        </div>
      ) : (
        <></>
      )}
    </div>
  );
}

interface ImagesListProps {
  images?: AssetData[];
  onDeleteImageClick?: (image: AssetData) => void;
}

function ImagesList({ images, onDeleteImageClick }: ImagesListProps) {
  const [openImageViewerModal, setOpenImageViewerModal] = useState(false);

  return (
    <div className="flex flex-col gap-4">
      <ImageViewerModal
        images={images || []}
        open={openImageViewerModal}
        onClose={() => setOpenImageViewerModal(false)}
      />
      {images?.length ? (
        <div className="flex flex-row flex-wrap gap-4">
          {images?.map((image) => (
            <div key={image.id} className="group relative">
              <img
                src={image.url}
                className="center h-16 w-16 cursor-pointer rounded-md object-contain"
                onClick={() => setOpenImageViewerModal(true)}
              />
              {onDeleteImageClick ? (
                <div className="pointer-events-none invisible absolute top-0 flex h-full w-full items-start justify-end group-hover:visible">
                  <IconButton
                    size="small"
                    color="negative"
                    onClick={() => onDeleteImageClick(image)}
                    className="pointer-events-auto absolute -right-2 -top-2"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </IconButton>
                </div>
              ) : (
                <></>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-gray-500">None</div>
      )}
    </div>
  );
}

interface TestimonialsProps {
  testimonials?: TestimonialsSectionItemData[];
  onEditClick: (testimonial: TestimonialsSectionItemData) => void;
  onDeleteClick: (testimonial: TestimonialsSectionItemData) => void;
}

function Testimonials({
  testimonials,
  onEditClick,
  onDeleteClick,
}: TestimonialsProps) {
  if (!testimonials?.length) {
    return <div className="text-gray-500">None</div>;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-row flex-wrap gap-4">
        {testimonials?.map((testimonial) => (
          <div key={testimonial.id} className="group relative">
            <Testimonial testimonial={testimonial} />
            <div className="invisible absolute top-0 flex h-full w-full items-start justify-end group-hover:visible">
              <IconButton
                size="small"
                onClick={() => onEditClick(testimonial)}
                className="absolute -right-2 -top-2"
              >
                <PencilIcon className="h-4 w-4" />
              </IconButton>
              <IconButton
                size="small"
                color="negative"
                onClick={() => onDeleteClick(testimonial)}
                className="absolute -right-2 top-6"
              >
                <TrashIcon className="h-4 w-4" />
              </IconButton>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Testimonial({
  testimonial,
}: {
  testimonial: TestimonialsSectionItemData;
}) {
  return (
    <figure className="rounded-2xl bg-white shadow-lg ring-1 ring-gray-900/5 sm:col-span-2 xl:col-start-2 xl:row-end-1">
      <blockquote className="text min-w-[300px] max-w-md p-2 font-semibold leading-6 tracking-tight text-gray-900 sm:p-4 sm:leading-8">
        <QuillField value={testimonial.body} readOnly noDesign />
      </blockquote>
      {testimonial.author ? (
        <figcaption className="flex flex-wrap items-center gap-x-4 gap-y-4 border-t border-gray-900/10 px-6 py-4 sm:flex-nowrap">
          <img
            className="h-10 w-10 flex-none rounded-full bg-gray-50"
            src={testimonial.author?.image?.url}
            alt=""
          />
          <div className="flex-auto">
            <div className="font-semibold">
              <QuillField
                value={testimonial.author?.name || 'Anonymous'}
                readOnly
                noDesign
              />
            </div>

            <div className="text-gray-600">
              <QuillField
                value={testimonial.author?.title || 'anonymous'}
                readOnly
                noDesign
              />
            </div>
          </div>
          <img
            className="h-10 w-auto flex-none"
            src={testimonial.author?.logo?.url}
            alt=""
          />
        </figcaption>
      ) : (
        <></>
      )}
    </figure>
  );
}

interface PressMentionsProps {
  pressMentions?: PressMentionsSectionItemData[];
  onEditClick: (pressMention: PressMentionsSectionItemData) => void;
  onDeleteClick: (pressMention: PressMentionsSectionItemData) => void;
}

function PressMentions({
  pressMentions,
  onEditClick,
  onDeleteClick,
}: PressMentionsProps) {
  if (!pressMentions?.length) {
    return <div className="text-gray-500">None</div>;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-row flex-wrap gap-4">
        {pressMentions?.map((pressMention) => (
          <div key={pressMention.id} className="group relative">
            <PressMention pressMention={pressMention} />
            <div className="invisible absolute top-0 flex h-full w-full items-start justify-end group-hover:visible">
              <IconButton
                size="small"
                onClick={() => onEditClick(pressMention)}
                className="absolute -right-2 -top-2"
              >
                <PencilIcon className="h-4 w-4" />
              </IconButton>
              <IconButton
                size="small"
                color="negative"
                onClick={() => onDeleteClick(pressMention)}
                className="absolute -right-2 top-6"
              >
                <TrashIcon className="h-4 w-4" />
              </IconButton>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PressMention({
  pressMention,
}: {
  pressMention: PressMentionsSectionItemData;
}) {
  return (
    <figure className="rounded-2xl bg-white shadow-lg ring-1 ring-gray-900/5 sm:col-span-2 xl:col-start-2 xl:row-end-1">
      <blockquote className="text min-w-[300px] max-w-md p-6 font-semibold leading-6 tracking-tight text-gray-900 sm:p-8 sm:leading-8">
        <p>{`“${pressMention.body}”`}</p>
      </blockquote>
      {pressMention.name ? (
        <div className="flex flex-wrap items-center px-6 pb-6 sm:flex-nowrap sm:px-8">
          <div className="flex-auto">
            <div className="font-semibold">
              {`${pressMention?.name || 'Anonymous'}`}
            </div>
          </div>
        </div>
      ) : (
        <></>
      )}
      {pressMention.logo ? (
        <figcaption className="flex flex-wrap items-center gap-x-4 gap-y-4 border-t border-gray-900/10 px-6 py-4 sm:flex-nowrap">
          <img
            className="h-10 w-auto flex-none"
            src={pressMention?.logo?.url}
            alt=""
          />
        </figcaption>
      ) : (
        <></>
      )}
    </figure>
  );
}

interface ComparisonItemsProps {
  items?: QueryDocumentSnapshot<ContentItem, ContentItem>[];
  onEditClick: (item: QueryDocumentSnapshot<ContentItem, ContentItem>) => void;
  onDuplicateClick: (
    item: QueryDocumentSnapshot<ContentItem, ContentItem>,
  ) => void;
  onDeleteClick: (
    item: QueryDocumentSnapshot<ContentItem, ContentItem>,
  ) => void;
}

function ComparisonItems({
  items,
  onEditClick,
  onDuplicateClick,
  onDeleteClick,
}: ComparisonItemsProps) {
  if (!items?.length) {
    return <div className="text-gray-500"></div>;
  }

  return (
    <div className="flex flex-col gap-4 pt-6">
      <div className="flex flex-row flex-wrap gap-4">
        {items?.map((item) => (
          <div key={item.id} className="group relative">
            <ComparisonItem item={item} />
            <div className="invisible absolute top-0 flex h-full w-full items-start justify-end group-hover:visible">
              <div className="absolute -right-3 -top-2 flex flex-col gap-1">
                <IconButton size="small" onClick={() => onEditClick(item)}>
                  <PencilIcon className="h-4 w-4" />
                </IconButton>
                <IconButton size="small" onClick={() => onDuplicateClick(item)}>
                  <DocumentDuplicateIcon className="h-4 w-4" />
                </IconButton>
                <IconButton
                  size="small"
                  color="negative"
                  onClick={() => onDeleteClick(item)}
                >
                  <TrashIcon className="h-4 w-4" />
                </IconButton>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ComparisonItem({
  item,
}: {
  item: QueryDocumentSnapshot<ContentItem, ContentItem>;
}) {
  const itemData = item.data();
  return (
    <figure className="rounded-2xl bg-white shadow-lg ring-1 ring-gray-900/5 sm:col-span-2 xl:col-start-2 xl:row-end-1">
      <blockquote className="text min-w-[300px] max-w-md p-6 font-semibold leading-6 tracking-tight text-gray-900 sm:p-8 sm:leading-8">
        {extractTextFromQuillTrimmed(itemData.brandName)}
      </blockquote>
    </figure>
  );
}
